import { EventPayload } from "../../../common/types/Event";
import type { CandidateRejectionReason } from "../../../common/types/CandidateRejectionReason";
import { Options } from "../../../common/types/requestOptions";
import { TalentStatus } from "../../talents/types/common";
import { api } from "../../../app/api";
import { query } from "../../../app/query";
import { LocalDate } from "../../../common/types/commonTypes";

export interface JobAdApplicant {
  id: string;
  firstName: string;
  lastName: string;
  eezyAccepted?: boolean;
  status: TalentStatus;
  statusChanged?: LocalDate;
  appliedAt: number;
  hasCoverLetter: boolean;
  rejectionMessageSentAt?: number;
  blackListed: boolean;
  exclusivityEntryIds?: string[];
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
}

export interface JobAdApplicantsFetch {
  rows: JobAdApplicant[];
}

export interface JobApplicantsParameters {
  bundleId?: string;
  jobAdId?: string;
  cursor?: string;
  pageSize?: string;
  search?: string;
  excludeCandidates?: string;
}

export interface ApproveJobAdApplicantsParameters {
  jobAdId: string;
  applicantIds: string[];
  approve: boolean;
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
}

export interface SendJobAdApplicantRejectedMessageParameters {
  jobAdId: string;
  applicantIds: string[];
  message: string;
}

export interface JobAdApplicantsApproveChanged {
  applicantsIds: string[];
  eezyAccepted: boolean;
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
}

export interface JobAdApplicantRejectionMessageSent {
  applicantId: string;
}

export interface JobAdApplicantRejectedMessageDetails {
  userId?: string;
  firstName?: string;
  lastName?: string;
  at?: EpochTimeStamp;
  message?: string;
}

export type AssignmentCoverLetterParameters = {
  jobAdId: string;
  talentId: string;
};

export type AssignmentCoverLetter = {
  coverLetter: string;
};

export const getJobAdApplicants = async (
  parameters: JobApplicantsParameters,
  { requestId }: Options = {},
): Promise<JobAdApplicantsFetch> => {
  return api
    .get<JobAdApplicantsFetch>("/projections/qry/jobAdApplicants", {
      requestId,
      auth: true,
      queryParams: { ...parameters },
    })
    .then((res) => res.data);
};

export const approveJobAdApplicants = async (
  data: ApproveJobAdApplicantsParameters,
  { requestId }: Options = {},
) => {
  return await api
    .post<EventPayload<JobAdApplicantsApproveChanged>[]>(
      "/recruitment/cmd/approveJobAdApplicants",
      {
        requestId,
        auth: true,
        body: {
          ...data,
        },
      },
    )
    .then((res) => res.data);
};

export const sendJobAdApplicantRejectedMessage = async (
  data: SendJobAdApplicantRejectedMessageParameters,
  { requestId }: Options = {},
) => {
  return await api
    .post("/recruitment/cmd/sendJobAdApplicantRejectedMessage", {
      requestId,
      auth: true,
      body: {
        ...data,
      },
    })
    .then((res) => res.data);
};

export const getJobAdApplicantCoverLetter = (
  filters: AssignmentCoverLetterParameters,
  options: Options = {},
): Promise<AssignmentCoverLetter> =>
  query<AssignmentCoverLetterParameters, AssignmentCoverLetter>(
    "/projections/qry/jobAdApplicantCoverLetter",
    filters,
    options,
  );

export const getJobAdApplicantRejectedMessage = async (
  params: { jobAdId: string; talentId: string },
  { requestId }: Options = {},
): Promise<JobAdApplicantRejectedMessageDetails> =>
  await api
    .get<JobAdApplicantRejectedMessageDetails>(
      "/recruitment/qry/jobAdApplicantRejectedMessage",
      {
        requestId,
        auth: true,
        queryParams: params,
      },
    )
    .then((res) => res.data);
